import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { useValidateIntegrationForm } from '@modules/integrationsV2/hooks';
import {
  getAddIntegrationDrawerHeaderTitle,
  trackIntegrationEvent,
} from '@modules/integrationsV2/utils';
import { encodeOAuthState } from '@modules/integrationsV2/utils/encodeOAuthState';
import { GetAddIntegrationFormDrawerContentProps } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { Box, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDirection,
} from 'generated/graphql';
import querystring from 'querystring';
import { ChangeEvent, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { getSanitizedUrl } from 'v2/core/utils/sanitizeUrl';

const baseUrl = `https://www.zopim.com/oauth2/authorizations/new`;

type ZendeskChatFormData = {
  zendeskClientID: string;
  zendeskClientSecret: string;
  zendeskSubDomain: string;
};

const initialState: ZendeskChatFormData = {
  zendeskClientID: '',
  zendeskClientSecret: '',
  zendeskSubDomain: '',
};

export const ZendeskChatFormDrawerContent = ({
  integrationType,
  integrationChannel,
  onDrawerClose,
  integrationID,
}: GetAddIntegrationFormDrawerContentProps) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();
  const { closeModal } = useMuiModalContext();

  const [formData, setFormData] = useState<ZendeskChatFormData>(initialState);
  const [descriptionInput, setDescriptionInput] = useState('');

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const isReauthorizeForm = !!integrationID;

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    shouldValidateDescription: !isReauthorizeForm,
    channelType: IntegrationChannelType.ZendeskChat,
    integrationType,
    hasValidFormFields:
      !!formData.zendeskClientID &&
      !!formData.zendeskClientSecret &&
      !!formData.zendeskSubDomain,
    descriptionInput,
    currentDescription: '',
    onValidated: async () => {
      const queryParams = {
        response_type: 'token',
        client_id: formData.zendeskClientID,
        scope: 'read',
        subdomain: formData.zendeskSubDomain,
        redirect_uri: `${window.location.origin}/settings/integrations`,
        state: encodeOAuthState({
          channelType: IntegrationChannelType.ZendeskChat,
          direction: IntegrationDirection.Inbound,
          data: {
            subDomain: formData.zendeskSubDomain,
            tenantSlug,
            description: descriptionInput.trim(),
          },
          integrationType,
          integrationID,
        }),
      };

      const redirectZendeskUrl = getSanitizedUrl(
        `${baseUrl}?${querystring.stringify(queryParams)}`,
      );

      const windowURL = window.open(redirectZendeskUrl, '_self');

      if (windowURL) {
        closeModal();
        trackIntegrationEvent({
          eventName: 'Integration Added',
          integrationChannelType: IntegrationChannelType.ZendeskChat,
        });
      }
    },
  });

  return (
    <Stack height="100%" justifyContent="space-between">
      <Box>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={getAddIntegrationDrawerHeaderTitle(
            integrationChannel,
            integrationType,
          )}
          isReauthorizeForm={isReauthorizeForm}
        />
        <Stack p={3} spacing={4}>
          <Stack spacing={2}>
            <TextField
              required
              label="Zendesk Client ID"
              placeholder="Enter Client ID"
              onChange={handleOnChange}
              name="zendeskClientID"
              value={formData.zendeskClientID}
            />
            <TextField
              required
              label="Zendesk Client Secret"
              placeholder="Enter Cient Secret"
              onChange={handleOnChange}
              name="zendeskClientSecret"
              value={formData.zendeskClientSecret}
            />
            <TextField
              required
              label="Zendesk Sub Domain"
              placeholder="Enter Sub Domain"
              onChange={handleOnChange}
              name="zendeskSubDomain"
              value={formData.zendeskSubDomain}
            />
          </Stack>
          {!isReauthorizeForm && (
            <IntegrationDescriptionForm
              value={descriptionInput}
              onChange={setDescriptionInput}
              isRequired={isDescriptionRequired}
              validatedDescriptionMessage={validatedDescriptionMessage}
              isValidationInProgress={isValidatingDescription}
              descriptionValidForQuestions={descriptionValidForQuestions}
              isPreValidatedDescription={isPreValidatedDescription}
              integrationType={integrationType}
            />
          )}
        </Stack>
      </Box>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        buttonText={isReauthorizeForm ? 'Reauthorize' : 'Integrate'}
        isDisabled={isDisabled}
        isLoading={false}
      />
    </Stack>
  );
};
